import BaseMap from 'components/Map/BaseMap'
import CleaningRoutesLayer from 'components/Map/CleaningRoutesLayer'
import CleaningStartFinishPoints from 'components/Map/CleaningStartFinishPoints'
import CleaningZonesLayer from 'components/Map/CleaningZonesLayer'
import DrawModules from 'components/Map/DrawModules'
import Layers from 'components/Map/Layers'
import MapContainer from 'components/Map/MapContainer'
import RestrictedZonesLayer from 'components/Map/RestrictedZonesLayer'
import RobotsLayer from 'components/Map/RobotsLayer'
import TransferRoutesLayer from 'components/Map/TransferRoutesLayer'
import Widgets from 'components/Map/Widgets'
import ApriorMapToggle from 'components/Map/widgets/ApriorMapToggle'
import LayerList from 'components/Map/widgets/LayerList'
import Legend from 'components/Map/widgets/Legend/Legend'
import MapToolbar from 'components/Map/widgets/MapToolbar'
import Measurment from 'components/Map/widgets/Measurment'
import Search from 'components/Map/widgets/Search/Search'
import ZoomToRobot from 'components/Map/widgets/ZoomToRobot'
import { Feature as GeoJSONFeature } from 'geojson'
import useAllRobots from 'hooks/useAllRobots'
import useMission from 'hooks/useMission'
import isEqual from 'lodash.isequal'
import { Extent, createEmpty, extend, isEmpty } from 'ol/extent'
import GeoJSON from 'ol/format/GeoJSON'
import { fromLonLat } from 'ol/proj'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import useMapStore from 'store/map'
import useMissionStore from 'store/mission'
import { MISSION_MAP_DRAWING_MODES } from 'utils/constants'
import { ICleaningZone, ICleaningZonePoint, ITrajectory } from 'utils/interfaces'
import { ApriorMapVersionId } from 'utils/types'
import { useShallow } from 'zustand/react/shallow'

import toolbarStyles from 'components/Map/widgets/MapToolbar.module.css'

const MissionMap = () => {
  const {
    mapMode,
    editingStartingPoint,
    editingEndingPoint,
    cleaningZoneStartingPoints,
    cleaningZoneEndingPoints,
    restrictZones,
    editingZone,
    cleaningZones,
    transferRoutes,
    missionPoints,
    editingTransfer,
    tempZone,
    tempStartingPoint,
    tempEndingPoint,
    tempVisualSettings,
    tempTransfer,
    cleaningRoutes,
    visualSettings,
    createFeature,
    editFeature,
  } = useMissionStore(
    useShallow(state => ({
      mapMode: state.mapMode,
      editingStartingPoint: state.editingStartingPoint,
      editingEndingPoint: state.editingEndingPoint,
      cleaningZoneStartingPoints: state.cleaningZoneStartingPoints,
      cleaningZoneEndingPoints: state.cleaningZoneEndingPoints,
      restrictZones: state.restrictZones,
      editingZone: state.editingZone,
      cleaningZones: state.cleaningZones,
      transferRoutes: state.transferRoutes,
      missionPoints: state.missionPoints,
      editingTransfer: state.editingTransfer,
      tempZone: state.tempZone,
      tempStartingPoint: state.tempStartingPoint,
      tempEndingPoint: state.tempEndingPoint,
      tempTransfer: state.tempTransfer,
      tempVisualSettings: state.tempVisualSettings,
      cleaningRoutes: state.cleaningRoutes,
      visualSettings: state.visualSettings,
      createFeature: state.createFeature,
      editFeature: state.editFeature,
    }))
  )
  const { data: robots = [] } = useAllRobots()
  const { data: missionInfoData } = useMission()
  const map = useMapStore(s => s.map)
  const tempApriorMapVersionId = useMapStore(s => s.tempApriorMapVersionId)
  const storeMissionId = useMissionStore(s => s.missionInfo?.id)
  const savedApriorMapVersionId = useMissionStore(s => s.missionInfo?.apriorMapVersionId)
  const selectedApriorMapVersionId =
    tempApriorMapVersionId === undefined ? savedApriorMapVersionId : tempApriorMapVersionId
  const missionId = missionInfoData?.mission.id
  const missionReady = !!missionId && missionId === storeMissionId
  const apriorMapExtentRef = useRef<{
    missionId?: number
    versionId?: ApriorMapVersionId | null
    extent: Extent | null
  }>({ extent: null })
  const initiallyCenteredMissionIdRef = useRef<number | null>(null)
  const [focusedOnRobot, setFocusedOnRobot] = useState(false)
  const missionRobot = robots.find(robot => robot.job?.missionId === missionId && robot.lastPosition)

  useEffect(() => setFocusedOnRobot(false), [missionId])

  const onFeatureCreated = (evt: { features: GeoJSONFeature[] }) => {
    createFeature(evt)
  }

  const onFeatureGeometryEdited = (evt: { features: GeoJSONFeature[]; action: string }) => editFeature(evt)

  const CheckChangesVisualSettings = () => {
    return !isEqual(tempVisualSettings, visualSettings) ? tempVisualSettings : visualSettings
  }

  const startingPointsData: ICleaningZonePoint[] = cleaningZoneStartingPoints.filter(
    point => point !== editingStartingPoint
  )
  const endingPointsData: ICleaningZonePoint[] = cleaningZoneEndingPoints.filter(point => point !== editingEndingPoint)

  const restrictZonesData = restrictZones.filter((restrictZone: any) => restrictZone.id !== editingZone?.id)

  const cleaningZonesData: ICleaningZone[] = cleaningZones.filter(
    (cleaningZone: any) => cleaningZone.id !== editingZone?.id
  )

  const transferRouteData = useMemo(() => {
    let routes: Array<ITrajectory> = []
    routes.push(...missionPoints.routes, ...transferRoutes)
    routes = routes.filter(route => route.id !== editingTransfer?.id)
    return routes
  }, [missionPoints.routes, transferRoutes, editingTransfer])

  const startPointData = useMemo(() => {
    if (missionPoints.startPoint !== missionPoints.editingPoint) {
      return missionPoints.startPoint
    }
  }, [missionPoints.startPoint, missionPoints.editingPoint])

  const finishPointData = useMemo(() => {
    if (missionPoints.finishPoint !== missionPoints.editingPoint) {
      return missionPoints.finishPoint
    }
  }, [missionPoints.finishPoint, missionPoints.editingPoint])

  const getMissionElementsExtent = useCallback((): Extent | null => {
    if (!map) return null

    const features: GeoJSONFeature[] = [
      ...cleaningZones,
      ...restrictZones,
      ...cleaningRoutes,
      ...transferRoutes,
      ...missionPoints.routes,
      ...cleaningZoneStartingPoints,
      ...cleaningZoneEndingPoints,
      ...(missionPoints.startPoint ? [missionPoints.startPoint] : []),
      ...(missionPoints.finishPoint ? [missionPoints.finishPoint] : []),
    ]
    const extent = createEmpty()
    const geojson = new GeoJSON()
    features.forEach(feature => {
      if (!feature.geometry) return
      const geometry = geojson.readGeometry(feature.geometry, {
        dataProjection: 'EPSG:4326',
        featureProjection: map.getView().getProjection(),
      })
      extend(extent, geometry.getExtent())
    })
    return isEmpty(extent) ? null : extent
  }, [
    map,
    cleaningZones,
    restrictZones,
    cleaningRoutes,
    transferRoutes,
    missionPoints,
    cleaningZoneStartingPoints,
    cleaningZoneEndingPoints,
  ])

  const fitExtent = useCallback(
    (extent: Extent) => {
      map?.getView().fit(extent, { padding: [50, 50, 50, 50], maxZoom: 18 })
      setFocusedOnRobot(false)
    },
    [map]
  )

  const getApriorMapExtent = useCallback((): Extent | null => {
    const current = apriorMapExtentRef.current
    return current.missionId === missionId && current.versionId === selectedApriorMapVersionId
      ? current.extent
      : null
  }, [missionId, selectedApriorMapVersionId])

  const onApriorMapExtentChange = useCallback(
    (extent: Extent | null, centerOnLoad: boolean) => {
      apriorMapExtentRef.current = {
        missionId,
        versionId: selectedApriorMapVersionId,
        extent,
      }
      if (!extent) return

      if (centerOnLoad) {
        fitExtent(extent)
        if (missionReady) initiallyCenteredMissionIdRef.current = missionId ?? null
      } else if (missionReady && initiallyCenteredMissionIdRef.current !== missionId && !getMissionElementsExtent()) {
        fitExtent(extent)
        initiallyCenteredMissionIdRef.current = missionId ?? null
      }
    },
    [fitExtent, getMissionElementsExtent, missionId, missionReady, selectedApriorMapVersionId]
  )

  useEffect(() => {
    if (missionReady && missionId && tempApriorMapVersionId !== undefined) {
      initiallyCenteredMissionIdRef.current = missionId
    }
  }, [missionId, missionReady, tempApriorMapVersionId])

  useEffect(() => {
    if (!missionReady || !missionId || !map || initiallyCenteredMissionIdRef.current === missionId) return

    const extent = getMissionElementsExtent() || getApriorMapExtent()
    if (extent) {
      fitExtent(extent)
      initiallyCenteredMissionIdRef.current = missionId
    } else if (!selectedApriorMapVersionId) {
      initiallyCenteredMissionIdRef.current = missionId
    }
  }, [fitExtent, getApriorMapExtent, getMissionElementsExtent, map, missionId, missionReady, selectedApriorMapVersionId])

  const centerMap = () => {
    if (map && missionRobot?.lastPosition && !focusedOnRobot) {
      const [longitude, latitude] = missionRobot.lastPosition.coordinates
      map.getView().setCenter(fromLonLat([longitude, latitude], map.getView().getProjection()))
      map.getView().setZoom(18)
      setFocusedOnRobot(true)
      return
    }
    const extent = getMissionElementsExtent() || getApriorMapExtent()
    if (extent) fitExtent(extent)
  }

  return (
    <MapContainer>
      <Layers>
        <BaseMap />
        <RobotsLayer robots={robots} />
        <DrawModules
          mode={mapMode}
          tempZone={tempZone}
          tempStartingPoint={tempStartingPoint}
          tempEndingPoint={tempEndingPoint}
          tempMissionPoint={missionPoints.tempPoint}
          tempTransferRoute={tempTransfer}
          onCreate={onFeatureCreated}
          onUpdate={onFeatureGeometryEdited}
        />
        <CleaningZonesLayer
          cleaningZones={cleaningZonesData}
          zoomToZone={true}
          visualSettings={CheckChangesVisualSettings()}
        />
        <RestrictedZonesLayer restrictedZones={restrictZonesData} visualSettings={CheckChangesVisualSettings()} />
        <CleaningStartFinishPoints startPoint={startPointData} finishPoint={finishPointData} />
        <CleaningRoutesLayer
          cleaningRoutes={cleaningRoutes}
          startingPoints={startingPointsData}
          endingPoints={endingPointsData}
          visualSettings={CheckChangesVisualSettings()}
        />
        <TransferRoutesLayer
          transferRoutes={transferRouteData}
          visualSettings={CheckChangesVisualSettings()}
          zoomToRoutes={!cleaningZonesData.length}
        />
      </Layers>
      <Widgets>
        <MapToolbar>
          <ZoomToRobot embedded onClick={centerMap} />
          <Measurment embedded />
          <div className={toolbarStyles.mapGroup}>
            <LayerList embedded />
            <ApriorMapToggle onExtentChange={onApriorMapExtentChange} />
          </div>
        </MapToolbar>
        <Legend visualSettings={visualSettings} />
        <Search />
      </Widgets>
    </MapContainer>
  )
}

export default MissionMap
